import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import FleetDataSet from 'models/fleet-management/FleetDataSet';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store';
import { selectFleetDashboardsData } from 'store/fleet-management/fleet-management-slice-selectors';
import CustomFleetDashboardIcon from '../CustomFleetDashboardIcon';
import CustomAlert from '@common/components/Feedback/CustomAlert';

const DatasetWidgetsInfo: React.FC<{ isEditing: boolean; dataset?: FleetDataSet }> = ({
  isEditing,
  dataset,
}) => {
  const { t } = useTranslation();

  const dashboards = useAppSelector((state) => selectFleetDashboardsData(state));

  if (dataset == null) return null;

  const dashboardsUsingDataset = dashboards.data?.filter((dashboard) =>
    dashboard.widgets.some((widget) => widget.dataset === dataset.id),
  );

  return (
    <React.Fragment>
      <Stack
        sx={(theme) => ({
          width: '100%',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          maxHeight: 'min(50%, 400px)',
          overflow: 'hidden',
          p: 2,
        })}
        spacing={2}
      >
        <Stack spacing={2}>
          <Typography variant="h2" sx={(theme) => ({ lineHeight: theme.spacing(5) })}>
            {t('title-dataset-widgets')}
          </Typography>
          {dashboardsUsingDataset != null && dashboardsUsingDataset.length > 0 ? (
            <CustomAlert
              header={t('info-dataset-in-following-widgets')}
              severity={isEditing ? 'warning' : 'info'}
            />
          ) : (
            <CustomAlert header={t('info-dataset-not-used')} severity="info" />
          )}
        </Stack>
        <Stack spacing={2} sx={{ overflow: 'auto' }}>
          {dashboardsUsingDataset?.map((dashboard) => (
            <Stack key={dashboard.id} spacing={1}>
              <Stack direction="row" spacing={2}>
                <CustomFleetDashboardIcon icon={dashboard.icon} />
                <Typography>{dashboard.name}</Typography>
              </Stack>
              <Stack spacing={1} pl={6}>
                {dashboard.widgets
                  .filter((w) => w.dataset === dataset.id)
                  .map((widget) => (
                    <Stack key={widget.id}>
                      <Typography variant="subtitle2">{widget.name}</Typography>
                    </Stack>
                  ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

export default DatasetWidgetsInfo;
